import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import confetti from 'canvas-confetti';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';

const PODIUM_COLORS = {
  0: { bg: '#FFD700', color: '#7a5100', label: '🥇' },
  1: { bg: '#C0C0C0', color: '#4a4a4a', label: '🥈' },
  2: { bg: '#CD7F32', color: '#5c2e00', label: '🥉' },
};

export default function ResultsTable({ results, yourWords = [], showTitle = true, excludeTop = 0, enableConfetti = false }) {
  const { t } = useTranslation();

  // Usar position si está disponible (resultados finales con top 100), si no, ordenar por score
  const sorted = [...results]
    .sort((a, b) => (a.position != null ? a.position - b.position : b.score - a.score))
    .filter((r) => !excludeTop || (r.position ?? 0) > excludeTop);
  const yourWordSet = new Set(yourWords.map((w) => w.canonical?.toLowerCase()).filter(Boolean));
  const hasPoints = sorted.some(
    (row) => yourWordSet.has(row.word?.toLowerCase()) && (row.score ?? 0) > 0
  );

  const confettiFired = useRef(false);
  useEffect(() => {
    if (hasPoints && enableConfetti && !confettiFired.current) {
      confettiFired.current = true;
      confetti({ particleCount: 150, spread: 80, origin: { y: 0.6 } });
    }
  }, [hasPoints, enableConfetti]);

  return (
    <Box>
      {showTitle && (
        <Typography variant="h6" fontWeight={700} gutterBottom>
          {t('results.title')}
        </Typography>
      )}
      <TableContainer sx={{ overflowX: 'auto', touchAction: 'pan-x pan-y' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'grey.50' } }}>
              <TableCell>#</TableCell>
              <TableCell>{t('results.tableWord')}</TableCell>
              <TableCell align="center">{t('results.tableCount')}</TableCell>
              <TableCell align="center">{t('results.tableScore')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map((row, idx) => {
              const position = row.position ?? idx + 1;
              const podium = PODIUM_COLORS[position - 1];
              const isYours = yourWordSet.has(row.word?.toLowerCase());
              const scores = row.score > 0;
              return (
                <TableRow
                  key={row.id || idx}
                  sx={{
                    bgcolor: podium ? podium.bg + '33' : isYours ? '#e3f2fd' : 'inherit',
                    opacity: scores ? 1 : 0.75,
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <TableCell>
                    {podium ? (
                      <Typography variant="body1">{podium.label}</Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">{position}</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography
                        fontWeight={podium ? 700 : 400}
                        sx={{ textTransform: 'capitalize' }}
                      >
                        {row.displayWord ?? row.word}
                      </Typography>
                      {isYours && (
                        <Chip label={t('results.yourWordBadge')} size="small" color="primary" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2">{row.amount ?? '-'}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography
                      fontWeight={scores ? 700 : 400}
                      color={podium ? podium.color : scores ? 'text.primary' : 'text.disabled'}
                    >
                      {scores ? row.score : '-'}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
